import { ExternalLink, Loader2, Newspaper, RefreshCw, Sparkles, TrendingDown, TrendingUp, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { addToWatchlist, fetchAiStats, fetchConsensusReliability, fetchDaytradeBackdrop, fetchPersonas, fetchStockAI, fetchStockDetail } from '../api';
import { isWatched, toggleWatch, WATCH_MAX } from '../alerts';
import { AIAnalysis, AiStats, CanSlimEntry, ConsensusReliability, MarketCharacter, OhlcvRow, PersonaView, ScoreHistoryPoint, StockDetail } from '../types';
import EarningsBadge from './EarningsBadge';
import { sigLabel } from '../interpret';   // 📖 화면 이름 사전(TERMS.md) — 값은 그대로, 글자만 바꿈

interface Props {
  ticker: string;
  name: string;
  onClose: () => void;
}

type Tab = '결론' | '타이밍' | '회사' | '상세' | '참고';
const TABS: Tab[] = ['결론', '타이밍', '회사', '상세', '참고'];

// 🎨 규격 V2(08-23) 공용 부품 — 색은 dc 토큰만(밝은 원색·보라 계열·그림자 금지).
//   버튼은 고스트/켜짐 두 가지, 칩은 높이 22·반경 6·11.5px 하나로 통일.
const BTN = 'inline-flex items-center gap-1.5 h-[26px] px-2 sm:h-[29px] sm:px-[11px] rounded-lg border text-[11.5px] sm:text-xs transition-colors';
const GHOST = `${BTN} border-[#202A3C] text-dc-ink2/90 font-medium hover:border-[#33405A] hover:text-dc-ink1 disabled:opacity-40 disabled:hover:border-[#202A3C]`;
const ON = `${BTN} border-dc-point/35 bg-dc-point/10 text-dc-point font-semibold`;
const CHIP = 'inline-flex items-center h-[22px] px-2 rounded-md border text-[11.5px] font-medium whitespace-nowrap';
const MINICHIP = 'inline-flex items-center h-[18px] px-1.5 rounded-[5px] border text-[10.5px] font-medium whitespace-nowrap';
const TONE = {
  point:    'border-dc-point/35 bg-dc-point/[0.08] text-dc-point',
  up:       'border-dc-up/40 bg-dc-up/[0.08] text-dc-up',
  upSoft:   'border-dc-up/30 bg-dc-up/[0.06] text-dc-up',
  down:     'border-dc-down/35 bg-dc-down/[0.07] text-dc-down',
  downSoft: 'border-dc-down/25 bg-dc-down/[0.05] text-dc-down',
  warn:     'border-dc-warn/40 bg-dc-warn/[0.08] text-dc-warn',
  ok:       'border-dc-ok/35 bg-dc-ok/[0.06] text-dc-ok',
  strong:   'border-dc-line2 bg-dc-bg2 text-dc-ink1',
  mute:     'border-dc-line1 bg-dc-bg1 text-dc-ink2',
  faint:    'border-dc-line1 bg-dc-base/70 text-dc-ink3',
} as const;
// 섹션 라벨(키커) · 카드 상자를 대신하는 헤어라인 구획 · 꼭 상자여야 할 때만 쓰는 인셋
const KICKER = 'text-[10.5px] font-[650] tracking-[.12em] text-dc-ink3';
const SECTION = 'border-b border-dc-hair pb-4 last:border-b-0';
const INSET = 'rounded-lg border border-dc-line1 bg-dc-bg1/60';
// 앰버 강조 박스(게이트·교훈·판결처럼 눈이 먼저 가야 하는 것)
const AMBERBOX = 'rounded-lg border border-dc-point/25 bg-dc-point/[0.04]';
// 접기 문구 표준 — ▸ 펼치기 / ▾ 접기 한 쌍만(규격 V2)
const FOLD = 'text-dc-point text-[10.5px]';

// 구조 시그널 — 강할수록 웜(상승색), 중간은 잉크 사다리, 약세 대기는 쿨, 위험만 경고색.
const SIGNAL_COLORS: Record<string, string> = {
  '신규진입': 'text-dc-up',
  '근접 구간': 'text-dc-up/80',
  '기회 탐색': 'text-dc-ink2',
  '관망(혼조)': 'text-dc-ink3',
  '변동성 안정 대기': 'text-dc-down',
  '위험': 'text-dc-warn',
};

// 행동결론 — 한국식 색 관례 유지(매수=웜 / 비중축소=쿨), 톤만 저채도로.
const ACTION_COLORS: Record<string, string> = {
  '매수': TONE.up,
  '분할매수': TONE.upSoft,
  '관망': TONE.mute,
  '비중축소': TONE.down,
};

// 시나리오 — 강세=웜, 약세=쿨(뜻은 그대로, 채도만 낮춤).
const SCENARIO_COLORS: Record<string, { bar: string; text: string }> = {
  '강세': { bar: 'bg-dc-up', text: 'text-dc-up' },
  '중립': { bar: 'bg-dc-ink3', text: 'text-dc-ink2' },
  '약세': { bar: 'bg-dc-down', text: 'text-dc-down' },
};

// 룰 시그널(객관 추세 구조)과 AI 행동결론을 같은 척도(+1 적극 / 0 중립 / -1 신중)로 환산.
// 상세 화면에서 AI 결론을 '리드'로 두고, 구조 신호와 갈릴 때 정직하게 설명하기 위함.
const _SIGNAL_STANCE: Record<string, number> = {
  '신규진입': 1, '완벽한추세': 1, '강한추세': 1,
  '추세형성': 0, '단기추세': 0,
  '관망': -1, '단기관망': -1, '위험': -1,
};
const _ACTION_STANCE: Record<string, number> = {
  '매수': 1, '분할매수': 1, '관망': 0, '비중축소': -1,
};
const signalStance = (s?: string): number => (s ? _SIGNAL_STANCE[s] ?? 0 : 0);
const actionStance = (a?: string): number => (a ? _ACTION_STANCE[a] ?? 0 : 0);

function fmt(v: number | null | undefined, digits = 0): string {
  if (v == null) return '─';
  return v.toLocaleString('ko-KR', { maximumFractionDigits: digits });
}

// ── 가격 차트 (종가 라인 + MA) ──────────────────────────────────
function PriceChart({ data }: { data: OhlcvRow[] }) {
  const chartData = data.map((d) => ({
    date: d.date.slice(5),
    close: d.close,
    ma5: d.ma5,
    ma20: d.ma20,
    ma60: d.ma60,
    high: d.high,
    low: d.low,
  }));

  return (
    <ResponsiveContainer width="100%" height={160}>
      <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#141B28" />
        <XAxis dataKey="date" tick={{ fill: '#5E6B82', fontSize: 10.5 }} interval={9} />
        <YAxis tick={{ fill: '#5E6B82', fontSize: 10.5 }} domain={['auto', 'auto']} />
        <Tooltip
          contentStyle={{ background: '#101623', border: '1px solid #1C2534', fontSize: 11.5 }}
          labelStyle={{ color: '#9DA9BE' }}
          itemStyle={{ color: '#E9EFF8' }}
        />
        <Line type="monotone" dataKey="close" stroke="#E9EFF8" dot={false} strokeWidth={1.5} />
        <Line type="monotone" dataKey="ma5" stroke="#DCA85E" dot={false} strokeWidth={1} />
        <Line type="monotone" dataKey="ma20" stroke="#9DA9BE" dot={false} strokeWidth={1} />
        <Line type="monotone" dataKey="ma60" stroke="#5C8FD6" dot={false} strokeWidth={1} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ── RSI 차트 ───────────────────────────────────────────────────
function RsiChart({ data }: { data: OhlcvRow[] }) {
  const chartData = data.map((d) => ({ date: d.date.slice(5), rsi: d.rsi }));
  return (
    <ResponsiveContainer width="100%" height={80}>
      <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#141B28" />
        <XAxis dataKey="date" tick={{ fill: '#5E6B82', fontSize: 10.5 }} interval={9} />
        <YAxis domain={[0, 100]} tick={{ fill: '#5E6B82', fontSize: 10.5 }} />
        <Tooltip
          contentStyle={{ background: '#101623', border: '1px solid #1C2534', fontSize: 11.5 }}
          labelStyle={{ color: '#9DA9BE' }}
        />
        <Line type="monotone" dataKey="rsi" stroke="#9DA9BE" dot={false} strokeWidth={1.5} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ── MACD 차트 ─────────────────────────────────────────────────
function MacdChart({ data }: { data: OhlcvRow[] }) {
  const chartData = data.map((d) => ({
    date: d.date.slice(5),
    macd: d.macd,
    signal: d.macd_signal,
    hist: d.macd_hist,
  }));
  return (
    <ResponsiveContainer width="100%" height={80}>
      <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#141B28" />
        <XAxis dataKey="date" tick={{ fill: '#5E6B82', fontSize: 10.5 }} interval={9} />
        <YAxis tick={{ fill: '#5E6B82', fontSize: 10.5 }} />
        <Tooltip
          contentStyle={{ background: '#101623', border: '1px solid #1C2534', fontSize: 11.5 }}
          labelStyle={{ color: '#9DA9BE' }}
        />
        <Bar dataKey="hist" fill="#2A3448" />
        <Line type="monotone" dataKey="macd" stroke="#DCA85E" dot={false} strokeWidth={1.5} />
        <Line type="monotone" dataKey="signal" stroke="#5C8FD6" dot={false} strokeWidth={1} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ── CAN SLIM 항목 행 ──────────────────────────────────────────
function CanSlimRow({ letter, entry }: { letter: string; entry: CanSlimEntry }) {
  const noData = entry.score === null;
  // 막대 채움은 앰버(달성)/회색(미달) 두 가지뿐 — 초록·노랑 3단 채움은 08-23 은퇴(규격 V2).
  const barColor = entry.score != null
    ? entry.score >= 70 ? 'bg-dc-point' : 'bg-dc-ink3'
    : '';
  return (
    <div className="flex items-center gap-2 text-[11.5px] py-0.5">
      <span className="w-4 text-dc-ink2 font-mono font-bold shrink-0">{letter}</span>
      {noData ? (
        <span className="flex-1 text-dc-ink3 italic">[데이터 미확보]</span>
      ) : (
        <>
          <div className="w-20 bg-dc-hair rounded-full h-1.5 shrink-0">
            <div className={`${barColor} h-1.5 rounded-full transition-all`} style={{ width: `${entry.score}%` }} />
          </div>
          <span className="w-6 text-right font-mono text-dc-ink1 shrink-0">{entry.score}</span>
          <span className="text-dc-ink3 truncate">{entry.label}</span>
        </>
      )}
      {entry.detail && (
        <span className="text-dc-ink3 text-[10.5px] truncate ml-auto shrink-0 max-w-[140px] flex items-center gap-1">
          {entry.detail.includes('장중 보정') && (
            <span className={`shrink-0 ${MINICHIP} ${TONE.warn}`}>장중</span>
          )}
          <span className="truncate">{entry.detail}</span>
        </span>
      )}
    </div>
  );
}

// ── 지표 카드 ─────────────────────────────────────────────────
function MetricCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className={`${INSET} px-3 py-2.5 flex flex-col gap-0.5`}>
      <span className="text-dc-ink3 text-[10.5px]">{label}</span>
      <span className="text-dc-ink1 text-[13px] font-mono font-semibold">{value}</span>
      {sub && <span className="text-dc-ink3 text-[10.5px]">{sub}</span>}
    </div>
  );
}

// ── 재무 표시 ─────────────────────────────────────────────────
function wonAbbr(v: number | null | undefined): string {
  if (v == null) return '─';
  const abs = Math.abs(v);
  if (abs >= 1e12) return `${(v / 1e12).toFixed(1)}조`;
  if (abs >= 1e8) return `${Math.round(v / 1e8).toLocaleString('ko-KR')}억`;
  return v.toLocaleString('ko-KR');
}

const RECO_LABEL: Record<string, { ko: string; cls: string }> = {
  strong_buy: { ko: '적극매수', cls: TONE.up },
  buy:        { ko: '매수',     cls: TONE.upSoft },
  hold:       { ko: '중립',     cls: TONE.mute },
  sell:       { ko: '매도',     cls: TONE.downSoft },
  strong_sell:{ ko: '적극매도', cls: TONE.down },
};

function FinCell({ label, value, good }: { label: string; value: string; good?: boolean }) {
  return (
    <div className={`${INSET} px-2.5 py-2 flex flex-col gap-0.5`}>
      <span className="text-dc-ink3 text-[10.5px]">{label}</span>
      <span className={`text-[13px] font-mono font-semibold ${good === undefined ? 'text-dc-ink1' : good ? 'text-dc-up' : 'text-dc-down'}`}>
        {value}
      </span>
    </div>
  );
}

// ── 점수 이력 타임라인 ─────────────────────────────────────────
// 세로 칩 색 — 초록→빨강 5단은 은퇴. 위는 앰버, 가운데는 잉크 사다리, 바닥은 약세색(쿨).
function scoreTier(s: number | null): string {
  if (s == null) return 'bg-dc-hair';
  if (s >= 80) return 'bg-dc-point';
  if (s >= 60) return 'bg-dc-ink2';
  if (s >= 40) return 'bg-dc-ink3';
  if (s >= 20) return 'bg-dc-ink3/50';
  return 'bg-dc-down/70';
}

function ScoreTimeline({ history }: { history: ScoreHistoryPoint[] }) {
  if (!history || history.length === 0) return null;
  return (
    <div className={SECTION}>
      <div className="flex items-center justify-between mb-1.5">
        <p className={KICKER}>최근 {history.length}일 추세점수 추이</p>
        <span className="text-dc-ink3 text-[10.5px]">색 = 점수 등급</span>
      </div>
      <div className="flex gap-0.5">
        {history.map((p) => (
          <div
            key={p.date}
            className={`flex-1 h-6 rounded-sm ${scoreTier(p.score)} relative group cursor-default`}
            title={`${p.date.slice(5)} · ${p.score ?? '─'}점 · ${sigLabel(p.signal)}`}
          >
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10 whitespace-nowrap bg-dc-bg1 border border-dc-line1 rounded-md px-1.5 py-0.5 text-[10.5px] text-dc-ink1">
              {p.date.slice(5)} · {p.score ?? '─'}점
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-1 text-[10.5px] text-dc-ink3">
        <span>{history[0].date.slice(5)}</span>
        <span>{history[history.length - 1].date.slice(5)}</span>
      </div>
    </div>
  );
}

// ── 거장 투자자 렌즈 (옵션·온디맨드 토글) ──────────────────────
const STANCE_CLS: Record<string, string> = { '관심': 'text-dc-up', '중립': 'text-dc-ink2', '회피': 'text-dc-down' };

function PersonaLens({ ticker }: { ticker: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<PersonaView[] | null>(null);
  const [loading, setLoading] = useState(false);
  const toggle = () => {
    if (!open && data === null && !loading) {
      setLoading(true);
      fetchPersonas(ticker)
        .then((r) => setData(r.personas || []))
        .catch(() => setData([]))
        .finally(() => setLoading(false));
    }
    setOpen((o) => !o);
  };
  return (
    <div>
      <button
        onClick={toggle}
        className={`w-full flex items-center gap-1.5 text-[11.5px] text-dc-ink2 hover:text-dc-ink1 px-2.5 py-2 ${INSET} hover:border-dc-line2 transition-colors`}
      >
        🎩 거장 투자자 관점 <span className="text-dc-ink3 font-normal">(AI가 흉내낸 버핏·버리·린치·우드 관점 · 재미·참고용)</span>
        <span className={`${FOLD} ml-auto`}>{open ? '▾ 접기' : '▸ 펼치기'}</span>
      </button>
      {open && (
        <div className="mt-2">
          {loading ? (
            <p className="flex items-center gap-1.5 text-dc-ink2 text-[11.5px] py-2"><Loader2 size={12} className="animate-spin" /> 거장들이 보는 중...</p>
          ) : data && data.length > 0 ? (
            <div className="space-y-1.5">
              {data.map((p, i) => (
                <div key={i} className={`${INSET} px-2.5 py-1.5`}>
                  <p className="text-[11.5px] font-medium text-dc-ink1">
                    {p.name} <span className={STANCE_CLS[p.stance] ?? 'text-dc-ink2'}>[{p.stance}]</span>
                  </p>
                  <p className="text-[11.5px] text-dc-ink2 leading-snug">{p.view}</p>
                </div>
              ))}
              <p className="text-dc-ink3 text-[10.5px]">※ 서로 반대되는 투자 철학의 관점 · 참고용 · 점수 미반영</p>
            </div>
          ) : (
            <p className="text-dc-ink3 text-[11.5px] py-2">관점 분석을 불러올 수 없습니다.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── AI 분석 로딩 스켈레톤 (빈 스피너 대신 결과 모양의 자리표시) ───────
function MeceSkeleton() {
  return (
    <div className="space-y-3 animate-pulse">
      <div className="flex items-center gap-2">
        <div className="h-6 w-16 bg-dc-bg2 rounded-md" />
        <div className="h-5 w-20 bg-dc-bg2/70 rounded-md" />
      </div>
      <div className="h-4 w-3/4 bg-dc-bg2 rounded-md" />
      <div className="space-y-1.5">
        <div className="h-3 w-full bg-dc-bg2/60 rounded-md" />
        <div className="h-3 w-11/12 bg-dc-bg2/60 rounded-md" />
        <div className="h-3 w-4/5 bg-dc-bg2/60 rounded-md" />
      </div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <div className="h-14 bg-dc-bg2/40 rounded-lg" />
        <div className="h-14 bg-dc-bg2/40 rounded-lg" />
      </div>
      <div className="flex items-center gap-1.5 text-dc-ink3 text-[11.5px] pt-1">
        <Loader2 size={12} className="animate-spin" /> 6명 분석가 토론 중… (최대 1분, 과부하 시 자동 재시도)
      </div>
    </div>
  );
}


// ── MECE 분석 렌더 ────────────────────────────────────────────
function MeceAnalysis({ ai, signal, aiStats }: { ai: AIAnalysis; signal?: string; aiStats?: AiStats | null }) {
  const actionCls = ACTION_COLORS[ai.action] ?? TONE.mute;
  // ④ 이 결론(action)의 최근 적중률 — '매수'·'분할매수'는 매수 스탠스로 묶임
  const stanceKey = (ai.action === '매수' || ai.action === '분할매수') ? '매수·분할매수' : ai.action;
  const stanceAgg = aiStats?.by_stance?.[stanceKey];
  return (
    <div className="space-y-4">
      {/* 한 줄 결론 + 확신도 */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`${CHIP} font-bold ${actionCls}`}>
            {ai.action}
          </div>
          {/* ④ 이 결론의 최근 적중률 — 맹신도 무시도 아닌 '실적 있는 조언' */}
          {stanceAgg && (stanceAgg.decided ?? 0) >= 3 && stanceAgg.hit_rate != null ? (
            <span className={`text-[10.5px] ${stanceAgg.hit_rate >= 55 ? 'text-dc-ok/85' : stanceAgg.hit_rate >= 45 ? 'text-dc-point/85' : 'text-dc-ink3'}`}
              title={`AI '${stanceKey}' 결론의 최근 적중률 (채점 ${stanceAgg.decided}건) · 멜트업 구간이면 베타 주의`}>
              최근 적중 {stanceAgg.hit_rate}%
            </span>
          ) : stanceAgg && (stanceAgg.observed ?? 0) >= 1 ? (
            <span className="text-[10.5px] text-dc-ink3" title="채점 표본 쌓이는 중">📊 수집 중</span>
          ) : null}
          {ai.conviction && (
            <span className={`${MINICHIP} ${
              ai.conviction === '강한 확신' ? TONE.point
                : ai.conviction === '약함/혼조' ? TONE.faint
                : TONE.mute}`}>
              확신 {ai.conviction}
            </span>
          )}
        </div>
        <p className="text-dc-ink1 text-[13px] font-semibold leading-snug">{ai.headline}</p>
        {/* 🅐 역풍 게이트(결정론) — 시장 역풍인데 AI가 매수·분할매수. AI 의견은 위에 그대로 두되, 하락장 매수 위험을 명시 */}
        {ai.gate?.type === '역풍' && (
          <div className={`mt-1.5 ${AMBERBOX} px-3 py-2 text-[11.5px] text-dc-point/90 leading-snug`}>
            ⚠️ <b>역풍 게이트</b> — 오늘 시장은 역풍(하락 우세)인데 AI 결론은 '{ai.gate.action}'입니다. 시장을 거스르는 진입이니
            <b> 1차 소액·분할</b>로 제한하고, AI가 든 하락 리스크(bear_case)를 먼저 확인하세요. <span className="text-dc-ink3">이 경고는 실측(역풍장에서 추세 초기 종목 부진)에 근거한 규칙 배너 — AI 판단과 별개.</span>
          </div>
        )}
        {/* ⑥ 확신도 → 사이징 가이드(결정론 매핑, AI 아님) — 확신 약한데 풀베팅 방지 */}
        {ai.conviction && (
          <p className="text-[11.5px] text-dc-ink3">
            💡 {ai.conviction === '강한 확신' ? '확신 강함 — 평소 비중까지 가능'
              : ai.conviction === '약함/혼조' ? '확신 약함 — 평소 비중의 절반 이하 권장'
              : '확신 중간 — 평소 비중의 2/3 정도'} <span className="text-dc-ink3">(사이징 가이드)</span>
          </p>
        )}
      </div>

      {/* 구조 ↔ AI 결론 정합 — 갈릴 때만. AI 결론을 '리드'로 두고 룰 시그널과의 차이를 정직하게 설명 */}
      {signal && signalStance(signal) !== actionStance(ai.action) && (() => {
        const structStronger = signalStance(signal) > actionStance(ai.action);
        return (
          <div className={`${AMBERBOX} px-3 py-2`}>
            <p className="text-[11.5px] leading-snug text-dc-ink2">
              <span className="font-bold text-dc-point">⚖️ 구조 ↔ AI 결론</span>{' '}
              <span className="text-dc-ink3">— 이 둘은 다른 걸 봅니다(구조=객관 추세, AI=종합 판단)</span><br />
              객관 추세 구조는 <span className="font-semibold text-dc-ink1">'{sigLabel(signal)}'</span>
              {structStronger ? '로 강하지만, ' : '로 약하지만, '}
              AI 종합결론은 <span className="font-semibold text-dc-ink1">'{ai.action}'</span>.{' '}
              {structStronger
                ? '구조가 좋아도 밸류·과열·리스크를 반영해 AI는 신중 — 이 화면은 AI 결론을 우선합니다.'
                : '구조는 아직 미완이나 반등·촉매를 본 적극론 — 들어간다면 분할로.'}
            </p>
          </div>
        );
      })()}

      {/* 지난 예측 검증 (Audit) */}
      {ai.audit && (() => {
        const v = ai.audit.verdict;
        const cls = v === '적중'
          ? 'border-dc-ok/30 bg-dc-ok/[0.05] text-dc-ok'
          : v === '빗나감'
          ? 'border-dc-warn/30 bg-dc-warn/[0.05] text-dc-warn'
          : 'border-dc-line1 bg-dc-bg1/60 text-dc-ink2';
        const ch = ai.audit.change_pct;
        return (
          <div className={`rounded-lg border px-2.5 py-2 ${cls}`}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[11.5px] font-bold">🕵️ 지난 예측 검증</span>
              <span className="text-[10.5px] px-1.5 py-px rounded-[5px] bg-dc-base/50 font-bold ml-auto">{v}</span>
            </div>
            <p className="text-[11.5px] leading-snug opacity-90">
              {ai.audit.days_ago != null ? `${ai.audit.days_ago}일 전 ` : ''}
              <span className="font-medium">{ai.audit.action}</span> 의견 ·{' '}
              {ai.audit.price_then.toLocaleString('ko-KR')} → {ai.audit.price_now.toLocaleString('ko-KR')}
              {' '}(<span className="font-mono">{ch >= 0 ? '+' : ''}{ch}%</span>)
            </p>
          </div>
        );
      })()}

      {/* 이번 교훈 (지난 예측 복기에서 AI가 뽑은 한 줄) */}
      {ai.lesson && ai.lesson.trim() && (
        <div className={`${AMBERBOX} px-3 py-2`}>
          <p className="text-dc-point text-[11.5px] font-bold mb-0.5 flex items-center gap-1">
            💡 이번 교훈 <span className="text-dc-ink3 font-normal">(지난 예측 복기)</span>
          </p>
          <p className="text-[11.5px] text-dc-ink2 leading-snug">{ai.lesson}</p>
        </div>
      )}

      {/* 핵심 변수 3개 */}
      {ai.key_variables?.length > 0 && (
        <div>
          <p className={`${KICKER} mb-1.5`}>핵심 변수</p>
          <div className="space-y-1">
            {ai.key_variables.map((v, i) => (
              <div key={i} className="flex items-start gap-2 text-[11.5px] text-dc-ink2">
                <span className="shrink-0 w-4 h-4 rounded-full bg-dc-bg2 text-dc-ink2 flex items-center justify-center text-[10.5px] font-bold mt-px">
                  {i + 1}
                </span>
                <span className="leading-snug">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 상승 논리 / 하락 리스크 */}
      <div className="grid grid-cols-1 gap-2">
        {ai.bull_case?.length > 0 && (
          <div className="rounded-lg border border-dc-up/25 bg-dc-up/[0.05] px-3 py-2.5">
            <p className="text-dc-up text-[11.5px] font-medium mb-1.5 flex items-center gap-1">
              <TrendingUp size={11} /> 상승 논리
            </p>
            <ul className="space-y-1">
              {ai.bull_case.map((b, i) => (
                <li key={i} className="text-[11.5px] text-dc-ink2 leading-snug flex gap-1.5">
                  <span className="text-dc-up/70 shrink-0">+</span>{b}
                </li>
              ))}
            </ul>
          </div>
        )}
        {ai.bear_case?.length > 0 && (
          <div className="rounded-lg border border-dc-down/25 bg-dc-down/[0.05] px-3 py-2.5">
            <p className="text-dc-down text-[11.5px] font-medium mb-1.5 flex items-center gap-1">
              <TrendingDown size={11} /> 하락 리스크
            </p>
            <ul className="space-y-1">
              {ai.bear_case.map((b, i) => (
                <li key={i} className="text-[11.5px] text-dc-ink2 leading-snug flex gap-1.5">
                  <span className="text-dc-down/70 shrink-0">−</span>{b}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* 6 전문가 토론 + 종합판결 */}
      {ai.analysts && (() => {
        // 역할별 6색(앰버·에메랄드·노랑·틸·하늘·보라)은 08-23 은퇴 — 여섯 칸 모두 같은 잉크 톤(규격 V2: 제3 포인트색·보라 금지).
        const roles = [
          { key: 'technical' as const,   icon: '🔧', label: '기술 분석가',   sub: '차트·보조지표', box: 'border-dc-line1 bg-dc-bg1/60', text: 'text-dc-ink1' },
          { key: 'trend' as const,       icon: '📈', label: '추세 분석가',   sub: '추세점수·RS',   box: 'border-dc-line1 bg-dc-bg1/60', text: 'text-dc-ink1' },
          { key: 'fundamental' as const, icon: '💰', label: '재무 분석가',   sub: 'DART 재무',     box: 'border-dc-line1 bg-dc-bg1/60', text: 'text-dc-ink1' },
          { key: 'supply' as const,      icon: '🌊', label: '수급 분석가',   sub: 'OBV·거래량',    box: 'border-dc-line1 bg-dc-bg1/60', text: 'text-dc-ink1' },
          { key: 'news' as const,        icon: '📰', label: '뉴스 분석가',   sub: '공시·뉴스',     box: 'border-dc-line1 bg-dc-bg1/60', text: 'text-dc-ink1' },
          { key: 'sentiment' as const,   icon: '🧠', label: '심리 분석가',   sub: '시장 내부 심리', box: 'border-dc-line1 bg-dc-bg1/60', text: 'text-dc-ink1' },
        ];
        const a = ai.analysts!;
        const any = roles.some((r) => a[r.key]) || a.verdict;
        if (!any) return null;
        return (
          <div>
            {/* ③ 결론 먼저 — 의장 판결은 노출, 6인 토론은 접기(원할 때만 펼침) */}
            {a.verdict && (
              <div className={`${AMBERBOX} px-3 py-2.5 mb-2`}>
                <p className="text-dc-point text-[11.5px] font-bold mb-1 flex items-center gap-1">🦁 의장 종합판결</p>
                <p className="text-[11.5px] text-dc-ink1 leading-snug font-medium">{a.verdict}</p>
              </div>
            )}
            <details className="group">
              <summary className="cursor-pointer list-none flex items-center gap-1.5 text-dc-ink3 text-[11.5px] font-medium select-none">
                🔬 전문가 6인 토론
                <span className={`${FOLD} group-open:hidden`}>▸ 펼치기</span>
                <span className={`${FOLD} hidden group-open:inline`}>▾ 접기</span>
              </summary>
              <div className="space-y-2 mt-1.5">
                {roles.map((r) => a[r.key] && (
                  <div key={r.key} className={`border rounded-lg px-3 py-2.5 ${r.box}`}>
                    <p className={`text-[11.5px] font-medium mb-1 flex items-center gap-1 ${r.text}`}>
                      {r.icon} {r.label} <span className="text-dc-ink3 font-normal">({r.sub})</span>
                    </p>
                    <p className="text-[11.5px] text-dc-ink2 leading-snug">{a[r.key]}</p>
                  </div>
                ))}
              </div>
            </details>
          </div>
        );
      })()}

      {/* 손익비 · 투자등급 (계산값) */}
      {ai.levels && (ai.levels.rr || ai.levels.grade) && (
        <div>
          <p className={`${KICKER} mb-1.5`}>
            손익비 · 투자등급 <span className="font-normal">(자동 계산)</span>
          </p>
          <div className="grid grid-cols-2 gap-1.5 text-[11.5px]">
            <div className={`${INSET} px-2.5 py-2`}>
              <span className="text-dc-ink3 block text-[10.5px]">손익비 (R/R)</span>
              <span className="text-dc-ink1 font-mono">
                {ai.levels.rr?.ratio != null
                  ? `1 : ${ai.levels.rr.ratio}`
                  : '신고가권(보류)'}
              </span>
              {ai.levels.rr?.upside_pct != null && ai.levels.rr?.downside_pct != null && (
                <span className="text-dc-ink3 block text-[10.5px]">
                  목표 +{ai.levels.rr.upside_pct}% / 손절 −{ai.levels.rr.downside_pct}%
                </span>
              )}
            </div>
            <div className={`${INSET} px-2.5 py-2`}>
              <span className="text-dc-ink3 block text-[10.5px]">장기 매력 / 진입</span>
              <span className="text-dc-ink1">{ai.levels.grade?.long_term ?? '─'}</span>
              <span className="text-dc-ink2 block text-[10.5px]">{ai.levels.grade?.entry ?? ''}</span>
            </div>
          </div>
        </div>
      )}

      {/* 확률 시나리오 */}
      {ai.scenarios?.length > 0 && (
        <div>
          <p className={`${KICKER} mb-1.5`}>확률 시나리오</p>
          <div className="space-y-2">
            {ai.scenarios.map((s, i) => {
              const c = SCENARIO_COLORS[s.name] ?? { bar: 'bg-dc-ink3', text: 'text-dc-ink2' };
              return (
                <div key={i} className={`${INSET} px-3 py-2.5`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[11.5px] font-bold ${c.text}`}>{s.name}</span>
                    <span className="text-dc-ink1 text-[11.5px] font-mono ml-auto">{s.probability}%</span>
                  </div>
                  <div className="w-full bg-dc-hair rounded-full h-1 mb-1.5">
                    <div className={`${c.bar} h-1 rounded-full`} style={{ width: `${s.probability}%` }} />
                  </div>
                  <p className="text-dc-ink2 text-[11.5px] leading-snug">
                    <span className="text-dc-ink3">조건:</span> {s.trigger}
                  </p>
                  <p className="text-dc-ink2 text-[11.5px] leading-snug">
                    <span className="text-dc-ink3">대응:</span> {s.plan}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 가격대별 대응 전략 */}
      {ai.price_plan && (
        <div>
          <p className={`${KICKER} mb-1.5`}>가격대별 대응 전략</p>
          <div className="grid grid-cols-1 gap-1.5 text-[11.5px]">
            <PlanRow label="진입" value={ai.price_plan.entry} color="text-dc-point" />
            <PlanRow label="손절" value={ai.price_plan.stop_loss} color="text-dc-down" />
            <PlanRow label="목표" value={ai.price_plan.target} color="text-dc-up" />
            <PlanRow label="보유" value={ai.price_plan.holding} color="text-dc-ink2" />
          </div>
        </div>
      )}

      {/* 포지션 3관점 (얼마나 공격적으로 들어갈까) */}
      {ai.risk_views && (
        <div>
          <p className={`${KICKER} mb-1.5`}>
            포지션 3관점 <span className="font-normal">(얼마나 공격적으로 들어갈까)</span>
          </p>
          <div className="space-y-1.5">
            {([['공격적', 'aggressive', 'text-dc-up'], ['중립', 'neutral', 'text-dc-ink2'], ['보수적', 'conservative', 'text-dc-down']] as const).map(([label, key, cls]) => (
              <div key={key} className={`${INSET} px-2.5 py-2`}>
                <span className={`text-[10.5px] font-bold ${cls}`}>{label}</span>
                <p className="text-[11.5px] text-dc-ink2 leading-snug">{ai.risk_views![key]}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 거장 투자자 렌즈 (옵션·온디맨드) */}
      <PersonaLens ticker={ai.ticker} />

      <p className="text-dc-ink3 text-[10.5px] leading-tight pt-1 border-t border-dc-hair">
        ※ 실시간 실적·뉴스 미반영. 추세·수급·가격 데이터 기반 분석이며 투자 판단의 책임은 본인에게 있습니다.
      </p>
    </div>
  );
}

function PlanRow({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className={`flex gap-2 ${INSET} px-2.5 py-2`}>
      <span className={`shrink-0 w-8 font-medium ${color}`}>{label}</span>
      <span className="text-dc-ink2 leading-snug">{value}</span>
    </div>
  );
}

// ── 포지션 사이징 계산기 ───────────────────────────────────────
function PositionSizer({ price, atr }: { price: number; atr: number | null | undefined }) {
  const [account, setAccount] = useState(() => Number(localStorage.getItem('ps_account')) || 10000000);
  const [riskPct, setRiskPct] = useState(() => Number(localStorage.getItem('ps_risk')) || 1.5);
  const defaultStop = Math.round(atr ? price - 2 * atr : price * 0.93);
  const [stop, setStop] = useState(defaultStop);

  useEffect(() => { setStop(Math.round(atr ? price - 2 * atr : price * 0.93)); }, [price, atr]);
  useEffect(() => { localStorage.setItem('ps_account', String(account)); }, [account]);
  useEffect(() => { localStorage.setItem('ps_risk', String(riskPct)); }, [riskPct]);

  const riskPerShare = price - stop;
  const valid = riskPerShare > 0 && account > 0 && riskPct > 0;
  const riskAmount = account * (riskPct / 100);
  const shares = valid ? Math.floor(riskAmount / riskPerShare) : 0;
  const buyAmount = shares * price;
  const maxLoss = shares * riskPerShare;

  const inputCls = 'w-full bg-dc-bg1 border border-[#202A3C] rounded-lg h-[29px] px-2 text-dc-ink1 text-[11.5px] font-mono text-right outline-none focus:border-dc-point/50';

  return (
    <div className={SECTION}>
      <p className={`${KICKER} mb-2`}>포지션 사이징 (손절 기준 매수 수량)</p>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <label className="flex flex-col gap-0.5 text-[10.5px] text-dc-ink3">계좌 금액
          <input type="number" value={account} onChange={(e) => setAccount(Number(e.target.value))} className={inputCls} />
        </label>
        <label className="flex flex-col gap-0.5 text-[10.5px] text-dc-ink3">리스크 %
          <input type="number" step="0.1" value={riskPct} onChange={(e) => setRiskPct(Number(e.target.value))} className={inputCls} />
        </label>
        <label className="flex flex-col gap-0.5 text-[10.5px] text-dc-ink3">진입가
          <input type="number" value={Math.round(price)} disabled className={`${inputCls} opacity-60`} />
        </label>
        <label className="flex flex-col gap-0.5 text-[10.5px] text-dc-ink3">손절가
          <input type="number" value={stop} onChange={(e) => setStop(Number(e.target.value))} className={inputCls} />
        </label>
      </div>
      {valid ? (
        <div className="grid grid-cols-3 gap-2">
          <div className={`${INSET} px-2.5 py-2 flex flex-col gap-0.5`}>
            <span className="text-dc-ink3 text-[10.5px]">매수 수량</span>
            <span className="text-dc-ink1 text-[13px] font-mono font-bold">{shares.toLocaleString('ko-KR')}주</span>
          </div>
          <div className={`${INSET} px-2.5 py-2 flex flex-col gap-0.5`}>
            <span className="text-dc-ink3 text-[10.5px]">매수 금액</span>
            <span className="text-dc-ink1 text-[13px] font-mono font-bold">{Math.round(buyAmount).toLocaleString('ko-KR')}</span>
            <span className="text-dc-ink3 text-[10.5px]">계좌의 {((buyAmount / account) * 100).toFixed(0)}%</span>
          </div>
          <div className={`${INSET} px-2.5 py-2 flex flex-col gap-0.5`}>
            <span className="text-dc-ink3 text-[10.5px]">최대 손실</span>
            <span className="text-dc-down text-[13px] font-mono font-bold">−{Math.round(maxLoss).toLocaleString('ko-KR')}</span>
            <span className="text-dc-ink3 text-[10.5px]">−{((price - stop) / price * 100).toFixed(1)}%</span>
          </div>
        </div>
      ) : (
        <p className="text-dc-warn text-[11.5px]">손절가가 진입가보다 낮아야 합니다.</p>
      )}
      <p className="text-dc-ink3 text-[10.5px] mt-1.5">계좌·리스크%는 저장됩니다. 손절가는 ATR 2배 기본값(수정 가능).</p>
    </div>
  );
}

// ── 메인 컴포넌트 ──────────────────────────────────────────────
export default function DetailPanel({ ticker, name, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('결론');
  const [detail, setDetail] = useState<StockDetail | null>(null);
  const [ai, setAi] = useState<AIAnalysis | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [loadingAi, setLoadingAi] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');
  const [errorAi, setErrorAi] = useState('');
  const [reliability, setReliability] = useState<ConsensusReliability | null>(null);
  const [character, setCharacter] = useState<MarketCharacter | null>(null);   // ② 오늘 장 성격(시장 맥락)
  const [added, setAdded] = useState(false);   // ⑤ 관심 담김 피드백
  const [wv, setWv] = useState(0);             // ⑤ 👁 감시 토글 즉시 반영
  const [aiStats, setAiStats] = useState<AiStats | null>(null);   // ④ AI 결론 적중률(꼬리표)
  const aiFetched = useRef(false);
  useEffect(() => { fetchDaytradeBackdrop().then((d) => setCharacter(d.character ?? null)).catch(() => {}); }, []);
  useEffect(() => { fetchAiStats().then(setAiStats).catch(() => {}); }, []);

  // 종목 바뀔 때마다 데이터 초기화 + 재로딩
  useEffect(() => {
    setDetail(null);
    setAi(null);
    setErrorDetail('');
    setErrorAi('');
    aiFetched.current = false;
    setAdded(false);
    setLoadingDetail(true);
    setTab('결론');

    fetchStockDetail(ticker)
      .then((d) => setDetail(d))
      .catch(() => setErrorDetail('상세 데이터 로딩 실패'))
      .finally(() => setLoadingDetail(false));

    fetchConsensusReliability().then(setReliability).catch(() => setReliability(null));
  }, [ticker]);

  // 결론 탭 진입 시 AI 호출 (최초 1회)
  useEffect(() => {
    if (tab === '결론' && detail && !ai && !aiFetched.current) {
      aiFetched.current = true;
      setLoadingAi(true);
      fetchStockAI(ticker)
        .then((a) => setAi(a))
        .catch(() => setErrorAi('AI 분석 호출 실패'))
        .finally(() => setLoadingAi(false));
    }
  }, [tab, detail, ai, ticker]);

  // 🔄 AI 재분석 — 캐시 무시하고 최신 모델로 새로 받음(예전 Groq 결과를 Claude로 교체할 때)
  const handleRefreshAi = () => {
    if (loadingAi) return;
    setLoadingAi(true);
    setErrorAi('');
    fetchStockAI(ticker, true)
      .then((a) => setAi(a))
      .catch(() => setErrorAi('AI 분석 호출 실패'))
      .finally(() => setLoadingAi(false));
  };

  const priceUp = detail ? detail.price_change_pct >= 0 : false;

  return (
    <div className="flex flex-col h-full bg-dc-bg0 border-l border-dc-hair">
      {/* 헤더 */}
      <div className="flex items-start justify-between px-4 py-3 border-b border-dc-hair shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-dc-ink1 font-[750] text-[14px]">{name}</span>
            <span className="text-dc-ink3 text-[11.5px] font-mono">{ticker}</span>
            {detail?.earnings_dday != null && <EarningsBadge dday={detail.earnings_dday} />}
          </div>
          {detail && (
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-[17.5px] font-mono font-[750] ${priceUp ? 'text-dc-up' : 'text-dc-down'}`}>
                {fmt(detail.current_price)}
              </span>
              <span className={`text-[11.5px] flex items-center gap-0.5 ${priceUp ? 'text-dc-up' : 'text-dc-down'}`}>
                {priceUp ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                {detail.price_change_pct > 0 ? '+' : ''}{detail.price_change_pct.toFixed(2)}%
              </span>
              {ai?.action ? (
                <span className="flex items-center gap-1">
                  <span className={`${MINICHIP} font-semibold ${ACTION_COLORS[ai.action] ?? TONE.mute}`}>
                    AI {ai.action}
                  </span>
                  <span className="text-[10.5px] text-dc-ink3" title="객관 추세 구조 지표(룰 기반). 이 화면은 AI 종합결론을 우선합니다.">
                    구조 {sigLabel(detail.signal)}{signalStance(detail.signal) !== actionStance(ai.action) ? ' ↔' : ''}
                  </span>
                </span>
              ) : (
                <span className={`text-[11.5px] font-medium ${SIGNAL_COLORS[detail.signal] ?? 'text-dc-ink2'}`}
                      title="객관 추세 구조(룰 기반). 종목을 열면 AI 종합결론이 우선 표시됩니다.">
                  구조 {sigLabel(detail.signal)}
                </span>
              )}
            </div>
          )}
        </div>
        {/* ⑤ 깔때기 마감 — 상세에서 바로 관심 담기 / 단타 감시 */}
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => addToWatchlist(ticker).then(() => setAdded(true)).catch(() => {})}
            title="관심종목에 담기" disabled={added}
            className={`${added ? ON : GHOST} whitespace-nowrap`}>
            {added ? '★ 담김' : '⭐ 관심'}
          </button>
          <button onClick={() => { const ok = toggleWatch(ticker, name); if (!ok) alert(`장중 감시는 최대 ${WATCH_MAX}개`); setWv((v) => v + 1); }}
            title="단타 탭에서 장중 감시(알림)" data-wv={wv}
            className={`${isWatched(ticker) ? ON : GHOST} whitespace-nowrap`}>
            👁 감시
          </button>
          <button onClick={onClose} className="text-dc-ink3 hover:text-dc-ink1 transition-colors ml-0.5">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* 보유 배너 (#50) — 보유 중이면 평가손익을 상단 전면에 */}
      {detail && detail.pnl_pct != null && (
        <div className={`px-4 py-2 shrink-0 border-b border-dc-hair flex items-center justify-between gap-3 ${detail.pnl_pct >= 0 ? 'bg-dc-up/[0.07]' : 'bg-dc-down/[0.07]'}`}>
          <div className="flex items-baseline gap-2">
            <span className="text-[10.5px] text-dc-ink2 shrink-0">보유</span>
            <span className={`text-[13px] font-bold font-mono ${detail.pnl_pct >= 0 ? 'text-dc-up' : 'text-dc-down'}`}>
              {detail.pnl_pct >= 0 ? '+' : ''}{detail.pnl_pct.toFixed(2)}%
            </span>
            {detail.pnl_amount != null && (
              <span className={`text-[11.5px] font-mono ${detail.pnl_pct >= 0 ? 'text-dc-up/85' : 'text-dc-down/85'}`}>
                {detail.pnl_amount >= 0 ? '+' : ''}{detail.pnl_amount.toLocaleString('ko-KR')}원
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-[10.5px] text-dc-ink2 flex-wrap justify-end">
            <span>평단 <b className="text-dc-ink1 font-mono">{fmt(detail.avg_price ?? 0)}</b></span>
            {detail.quantity != null && (
              <><span className="text-dc-ink3">·</span><span>{detail.quantity.toLocaleString('ko-KR')}주</span></>
            )}
            {detail.eval_amount != null && (
              <><span className="text-dc-ink3">·</span><span>평가 <b className="text-dc-ink1 font-mono">{detail.eval_amount.toLocaleString('ko-KR')}</b></span></>
            )}
          </div>
        </div>
      )}

      {/* 탭 — 활성은 앰버 밑줄 2px, 알약 배경 금지(규격 V2) */}
      <div className="flex border-b border-dc-hair shrink-0">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`flex-1 px-2.5 py-1.5 transition-colors ${
              tab === t
                ? 'text-[13px] font-bold text-dc-ink1 border-b-2 border-dc-point'
                : 'text-[13px] font-medium text-dc-ink3 hover:text-dc-ink2 border-b-2 border-transparent'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* 콘텐츠 */}
      <div className="flex-1 overflow-y-auto">
        {loadingDetail ? (
          <div className="flex items-center justify-center h-40 gap-2 text-dc-ink3 text-[13px]">
            <Loader2 size={16} className="animate-spin" /> 데이터 로딩 중...
          </div>
        ) : errorDetail ? (
          <div className="p-4 text-dc-warn text-[13px]">{errorDetail}</div>
        ) : !detail || detail.current_price == null ? (
          <div className="p-6 text-center text-dc-ink3 text-[13px]">데이터가 없는 종목입니다.</div>
        ) : (
          <>
            {/* ─── 결론 탭 ─── */}
            {tab === '결론' && (
              <div className="p-4 space-y-4">
                {/* ① 3초 요약 — 즉시 계산값으로 답, AI는 아래에서 도착 · ② 오늘 장 성격(시장 맥락) */}
                <div className={`${SECTION} space-y-1.5`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`${CHIP} border-dc-line1 bg-dc-bg1 ${SIGNAL_COLORS[detail.signal] ?? 'text-dc-ink2'}`}>{sigLabel(detail.signal)}</span>
                    <span className="text-dc-ink1 text-[13px]">종합 <b>{detail.score}</b><span className="text-dc-ink3 text-[10.5px]">/100</span></span>
                    {detail.rs_rank != null && <span className="text-dc-ink3 text-[11.5px]">RS {detail.rs_rank}</span>}
                    {detail.reversion_score != null && detail.reversion_score >= 65 && <span className="text-dc-ok text-[11.5px]">눌림 타이밍 양호</span>}
                    <span className="ml-auto text-dc-ink3 text-[10.5px]">즉시 계산값 · AI는 아래</span>
                  </div>
                  <p className="text-dc-ink2 text-[11.5px]">{
                    (detail.signal === '위험' || detail.signal === '관망') ? '추세 약함 — 관망 구간(성급한 진입 주의)'
                      : detail.score >= 80 ? '강한 상승추세 — 눌림 오면 노려볼 자리'
                      : detail.score >= 65 ? '상승추세 유효 — 진입 조건·손절 확인'
                      : (detail.reversion_score != null && detail.reversion_score >= 65) ? '추세는 약하나 과매도 반등(눌림) 구간'
                      : '중립 — 방향 대기'
                  }</p>
                  {character && (
                    <p className="text-[11.5px] pt-1 border-t border-dc-hair text-dc-ink2">
                      <span>{character.emoji} 오늘 {character.character}</span>
                      <span className="text-dc-ink3"> — {character.long_favorable === false ? '신규 진입 판단은 보수적으로' : character.long_favorable === true ? '시장 우호 — 진입 우위' : '시장 중립'}</span>
                    </p>
                  )}
                </div>

                {/* 점수 이력 타임라인 (상단 노출) */}
                <ScoreTimeline history={detail.score_history} />

                {/* AI 헤드라인 */}
                <div className={SECTION}>
                  <div className="flex items-center gap-1.5 mb-3">
                    <Sparkles size={13} className="text-dc-point" />
                    <span className="text-[10.5px] font-[650] tracking-[.12em] text-dc-point">AI 심층분석 (MECE)</span>
                    {ai?.ai_model && <span className="text-dc-ink3 text-[10.5px] ml-auto">{ai.ai_model}</span>}
                    <button
                      onClick={handleRefreshAi}
                      disabled={loadingAi}
                      title="AI 재분석 (캐시 무시 · 최신 모델로 새로 받기)"
                      className={`${GHOST} ${ai?.ai_model ? 'ml-1.5' : 'ml-auto'}`}
                    >
                      <RefreshCw size={11} className={loadingAi ? 'animate-spin' : ''} /> 재분석
                    </button>
                  </div>

                  {loadingAi ? (
                    <>
                      <p className="text-dc-ink3 text-[11.5px] mb-2">🤖 실데이터(재무·뉴스) 수집 중 — 보통 20~40초. 위 요약은 이미 계산값이라 지금 판단 가능해요.</p>
                      <MeceSkeleton />
                    </>
                  ) : errorAi ? (
                    <div className="text-dc-warn text-[13px]">{errorAi}</div>
                  ) : ai ? (
                    <MeceAnalysis ai={ai} signal={detail.signal} aiStats={aiStats} />
                  ) : null}
                </div>

                {/* 스코어 요약 */}
                <div className="grid grid-cols-2 gap-2">
                  <MetricCard
                    label="종합 점수"
                    value={`${detail.score} / 100`}
                    sub={detail.trend_score != null ? `추세 ${detail.trend_score} · RS ${detail.rs_rank ?? '─'}` : '추세 × 상대강도'}
                  />
                  <MetricCard
                    label="RS 순위"
                    value={detail.rs_rank != null ? `${detail.rs_rank}` : '─'}
                    sub={detail.rs_rank != null ? `전 종목 상위 ${100 - detail.rs_rank}%` : '스캔 후 표시'}
                  />
                  <MetricCard
                    label="RSI"
                    value={detail.rsi.toFixed(1)}
                    sub={detail.rsi < 30 ? '과매도' : detail.rsi > 70 ? '과매수' : '정상'}
                  />
                  <MetricCard
                    label="거래량 비율"
                    value={`${detail.volume_ratio.toFixed(2)}x`}
                    sub="20일 평균 대비"
                  />
                  <MetricCard
                    label="역추세 점수"
                    value={detail.reversion_score != null ? `${detail.reversion_score} / 100` : '─'}
                    sub={detail.reversion_signal ?? '반등(눌림) 관점'}
                  />
                </div>
                {detail.reversion_score != null && detail.reversion_score >= 65 && detail.score >= 70 && (
                  <p className="text-[11.5px] text-dc-ok border border-dc-ok/30 bg-dc-ok/[0.05] rounded-lg px-3 py-2 leading-snug">
                    ⭐ 추세도 강하고(종합 {detail.score}) 눌림 진입 타이밍도 좋음(역추세 {detail.reversion_score}) — 두 관점이 일치(confluence).
                  </p>
                )}

                {/* 네이버 컨센서스 (한국 종목 — 목표가·투자의견·추정PER) */}
                {detail.consensus && (detail.consensus.target || detail.consensus.recommend) && (
                  <div className={SECTION}>
                    <p className={`${KICKER} mb-3`}>
                      애널 컨센서스 <span className="font-normal">(네이버 · 참고용, 점수 미반영)</span>
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {detail.consensus.target != null && (
                        <FinCell label="목표주가" value={detail.consensus.target.toLocaleString('ko-KR')} />
                      )}
                      {detail.consensus.target != null && detail.current_price > 0 && (
                        <FinCell
                          label="현재가 대비"
                          value={`${(detail.consensus.target / detail.current_price - 1) * 100 >= 0 ? '+' : ''}${((detail.consensus.target / detail.current_price - 1) * 100).toFixed(1)}%`}
                          good={detail.consensus.target / detail.current_price - 1 >= 0}
                        />
                      )}
                      {detail.consensus.recommend != null && (
                        <FinCell label="투자의견 (5점, 높을수록 매수)" value={`${detail.consensus.recommend}`} />
                      )}
                      {detail.consensus.forward_per && (
                        <FinCell label="추정 PER" value={detail.consensus.forward_per} />
                      )}
                      {detail.consensus.foreign_ratio && (
                        <FinCell label="외인 소진율" value={detail.consensus.foreign_ratio} />
                      )}
                      {detail.consensus.div_yield && (
                        <FinCell label="배당수익률" value={detail.consensus.div_yield} />
                      )}
                    </div>
                    {/* 지난 컨센서스 검증 (이 종목) */}
                    {detail.consensus_audit && (() => {
                      const a = detail.consensus_audit!;
                      const cls = a.verdict === '도달' ? 'text-dc-ok' : a.verdict === '빗나감' ? 'text-dc-warn' : 'text-dc-point';
                      return (
                        <p className="text-[11.5px] text-dc-ink2 mt-2 pt-2 border-t border-dc-hair">
                          🕵️ {a.days_ago}일 전 목표 {a.target.toLocaleString('ko-KR')}(당시 {a.price_then.toLocaleString('ko-KR')}, +{a.implied_pct}%)
                          {' '}→ 현재 <b className={cls}>{a.verdict}</b>
                          {a.progress_pct != null && <span> · 진척 {a.progress_pct}%</span>}
                        </p>
                      );
                    })()}

                    {/* 누적 신뢰도 (전체 종목) */}
                    {reliability && reliability.samples > 0 && (
                      <p className="text-[11.5px] text-dc-ink3 mt-1.5">
                        📊 컨센서스 누적 신뢰도 (표본 {reliability.samples}): 도달 <b className="text-dc-ink2">{reliability.reach_rate}%</b> · 방향적중 <b className="text-dc-ink2">{reliability.direction_rate}%</b>
                        {reliability.avg_progress != null && <> · 평균진척 {reliability.avg_progress}%</>}
                        {' '}· 평균목표 +{reliability.avg_implied}%(낙관편향)
                        <span className="block text-dc-ink3 text-[10.5px] mt-0.5">※ 목표가는 12개월 기준 — 표본·기간 쌓여야 의미 있음.</span>
                      </p>
                    )}
                    <p className="text-dc-ink3 text-[10.5px] mt-2">※ 네이버 금융 컨센서스. 애널 목표가는 낙관 편향이 있어 참고만.</p>
                  </div>
                )}

                {/* 애널리스트 컨센서스 (yfinance — 주로 미국) */}
                {detail.analyst && detail.analyst.target_price != null && (
                  <div className={SECTION}>
                    <p className={`${KICKER} mb-3`}>애널리스트 컨센서스 (참고용)</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex flex-col">
                        <span className="text-dc-ink3 text-[10.5px]">목표주가</span>
                        <span className="text-dc-ink1 text-[13px] font-mono font-semibold">
                          {detail.analyst.target_price.toLocaleString('ko-KR')}
                        </span>
                      </div>
                      {(() => {
                        const up = (detail.analyst.target_price! / detail.current_price - 1) * 100;
                        return (
                          <div className="flex flex-col">
                            <span className="text-dc-ink3 text-[10.5px]">현재가 대비</span>
                            <span className={`text-[13px] font-mono font-semibold ${up >= 0 ? 'text-dc-up' : 'text-dc-down'}`}>
                              {up >= 0 ? '+' : ''}{up.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })()}
                      {detail.analyst.recommendation && RECO_LABEL[detail.analyst.recommendation] && (
                        <span className={`${CHIP} ${RECO_LABEL[detail.analyst.recommendation].cls}`}>
                          {RECO_LABEL[detail.analyst.recommendation].ko}
                        </span>
                      )}
                      {detail.analyst.count != null && (
                        <span className="text-dc-ink3 text-[11.5px]">애널 {detail.analyst.count}명</span>
                      )}
                    </div>
                    <p className="text-dc-ink3 text-[10.5px] mt-2">※ 점수엔 미반영. 애널 목표가는 낙관 편향이 있어 참고만.</p>
                  </div>
                )}
              </div>
            )}

            {/* ─── 타이밍 탭 ─── */}
            {tab === '타이밍' && (
              <div className="p-3 space-y-4">
                <div className={SECTION}>
                  <p className={`${KICKER} mb-1 px-1`}>가격 · 이동평균</p>
                  <PriceChart data={detail.ohlcv} />
                  <div className="flex gap-3 px-1 mt-1">
                    {[['MA5', '#DCA85E'], ['MA20', '#9DA9BE'], ['MA60', '#5C8FD6']].map(([l, c]) => (
                      <span key={l} className="flex items-center gap-1 text-[11.5px] text-dc-ink3">
                        <span className="inline-block w-3 h-0.5" style={{ background: c }} />
                        {l}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={SECTION}>
                  <p className={`${KICKER} mb-1 px-1`}>RSI (14)</p>
                  <RsiChart data={detail.ohlcv} />
                </div>
                <div className={SECTION}>
                  <p className={`${KICKER} mb-1 px-1`}>MACD (12,26,9)</p>
                  <MacdChart data={detail.ohlcv} />
                </div>
              </div>
            )}

            {/* ─── 회사 탭 ─── */}
            {tab === '회사' && (
              <div className="p-4 space-y-4">
                <div className={SECTION}>
                  <p className={`${KICKER} mb-3`}>CAN SLIM 분석</p>
                  <div className="space-y-1.5">
                    {(['C', 'A', 'N', 'S', 'L', 'I', 'M'] as const).map((key) => (
                      <CanSlimRow key={key} letter={key} entry={detail.canslim?.[key] ?? { score: null, label: '[데이터 미확보]', detail: '' }} />
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-dc-hair flex justify-between items-center">
                    <span className="text-dc-ink3 text-[11.5px]">평균 점수</span>
                    <span className="text-dc-ink1 text-[13px] font-mono font-bold">
                      {detail.canslim?.total ?? '─'}
                    </span>
                  </div>
                </div>
                <div className={`${INSET} px-3 py-2.5 text-[11.5px] text-dc-ink3 leading-snug`}>
                  C=분기EPS, A=연간ROE, N=52주신고가, S=거래량, L=MA배열, I=OBV기관추정, M=시장방향
                </div>
              </div>
            )}

            {/* ─── 상세 탭 ─── */}
            {tab === '상세' && (
              <div className="p-4 space-y-4">
                <div className={SECTION}>
                  <p className={`${KICKER} mb-2`}>기술적 지표</p>
                  <div className="grid grid-cols-2 gap-2">
                    <MetricCard
                      label="이동평균 배열"
                      value={detail.ma_alignment}
                    />
                    <MetricCard
                      label="MACD 교차"
                      value={detail.macd_cross ? '골든크로스' : '데드크로스'}
                    />
                    <MetricCard
                      label="섹터"
                      value={
                        !detail.sector || ['KOSPI', 'KOSDAQ', 'KONEX'].includes(detail.sector)
                          ? '정보 없음'
                          : detail.sector
                      }
                    />
                    <MetricCard
                      label="시그널"
                      value={sigLabel(detail.signal)}
                    />
                  </div>
                </div>

                <div className={SECTION}>
                  <p className={`${KICKER} mb-2`}>리스크 지표</p>
                  <div className="grid grid-cols-2 gap-2">
                    <MetricCard
                      label="ATR (14일)"
                      value={fmt(detail.risk?.atr)}
                      sub={detail.risk?.atr_pct != null ? `변동폭 ${detail.risk.atr_pct.toFixed(1)}%` : undefined}
                    />
                    <MetricCard
                      label="20일 연환산 변동성"
                      value={detail.risk?.volatility_20d != null ? `${detail.risk.volatility_20d.toFixed(1)}%` : '─'}
                    />
                    <MetricCard
                      label="52주 최고"
                      value={fmt(detail.risk?.week52_high)}
                      sub={detail.risk?.from_high_pct != null ? `현재 ${detail.risk.from_high_pct.toFixed(1)}%` : undefined}
                    />
                    <MetricCard
                      label="52주 최저"
                      value={fmt(detail.risk?.week52_low)}
                    />
                    <MetricCard
                      label="OBV (20일)"
                      value={detail.risk?.obv_20 != null ? detail.risk.obv_20.toLocaleString('ko-KR') : '─'}
                    />
                    <MetricCard
                      label="거래량"
                      value={detail.volume != null ? (detail.volume / 10000).toFixed(0) + '만' : '─'}
                    />
                  </div>
                </div>

                {/* 보조지표 (계산값) */}
                {detail.tech_extras && (detail.tech_extras.stoch || detail.tech_extras.ichimoku) && (
                  <div className={SECTION}>
                    <p className={`${KICKER} mb-2`}>
                      보조지표 <span className="font-normal">(계산값 · 엘리엇/피보 아님)</span>
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      {detail.tech_extras.stoch && (
                        <MetricCard
                          label="스토캐스틱"
                          value={detail.tech_extras.stoch.signal}
                          sub={`%K ${detail.tech_extras.stoch.k} / %D ${detail.tech_extras.stoch.d}`}
                        />
                      )}
                      {detail.tech_extras.ichimoku?.position && (
                        <MetricCard
                          label="일목균형표"
                          value={detail.tech_extras.ichimoku.position}
                          sub={detail.tech_extras.ichimoku.tk_cross ?? undefined}
                        />
                      )}
                      {detail.tech_extras.ichimoku?.cloud_top != null && detail.tech_extras.ichimoku?.cloud_bottom != null && (
                        <MetricCard
                          label="구름대 (지지/저항)"
                          value={`${fmt(detail.tech_extras.ichimoku.cloud_bottom)} ~ ${fmt(detail.tech_extras.ichimoku.cloud_top)}`}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* 투자자별 수급 (외국인/기관, 네이버 참고) */}
                {detail.investor_flow && detail.investor_flow.length > 0 && (
                  <div className={SECTION}>
                    <p className={`${KICKER} mb-2`}>
                      외국인·기관 수급 <span className="font-normal">(순매매량 주 · 네이버 참고)</span>
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-[11.5px]">
                        <thead>
                          <tr className="border-b border-dc-hair">
                            <th className="px-2 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap">날짜</th>
                            <th className="px-2 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap text-right">기관</th>
                            <th className="px-2 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap text-right">외국인</th>
                            <th className="px-2 py-2.5 text-dc-ink3 text-[10.5px] font-[650] tracking-[.12em] select-none whitespace-nowrap text-right">외인 보유율</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.investor_flow.slice(0, 5).map((f, i) => {
                            const cell = (v: number | null) => {
                              const c = (v ?? 0) > 0 ? 'text-dc-up' : (v ?? 0) < 0 ? 'text-dc-down' : 'text-dc-ink2';
                              return <span className={`font-mono ${c}`}>{(v ?? 0) > 0 ? '+' : ''}{v?.toLocaleString('ko-KR') ?? '─'}</span>;
                            };
                            return (
                              <tr key={i} className="border-b border-dc-hair/80 hover:bg-white/[0.015] transition-colors">
                                <td className="px-2 py-1.5 text-dc-ink2">{f.date}</td>
                                <td className="px-2 py-1.5 text-right">{cell(f.institution)}</td>
                                <td className="px-2 py-1.5 text-right">{cell(f.foreign)}</td>
                                <td className="px-2 py-1.5 text-right font-mono text-dc-ink2">{f.hold_ratio ?? '─'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-dc-ink3 text-[10.5px] mt-1">▲ 빨강=순매수, ▼ 파랑=순매도. 점수 미반영(참고치).</p>
                  </div>
                )}

                {/* 포지션 사이징 */}
                {detail.current_price > 0 && (
                  <PositionSizer price={detail.current_price} atr={detail.risk?.atr} />
                )}
              </div>
            )}

            {/* ─── 참고 탭 ─── */}
            {tab === '참고' && (
              <div className="p-4 space-y-4">
                {/* 뉴스 바로가기 */}
                <a
                  href={/^\d{6}$/.test(ticker)
                    ? `https://finance.naver.com/item/news.naver?code=${ticker}`
                    : `https://finance.yahoo.com/quote/${ticker}/news`}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-2 px-4 py-2.5 ${INSET} hover:border-dc-point/40 hover:bg-dc-bg2 transition-colors group`}
                >
                  <Newspaper size={15} className="text-dc-point shrink-0" />
                  <span className="text-[13px] text-dc-ink1">
                    {/^\d{6}$/.test(ticker) ? '네이버 금융 뉴스' : 'Yahoo Finance News'} 바로가기
                  </span>
                  <ExternalLink size={13} className="ml-auto text-dc-ink3 group-hover:text-dc-point" />
                </a>

                {/* 재무제표 (DART) */}
                <div className={SECTION}>
                  <p className={`${KICKER} mb-3`}>
                    재무제표 {detail.financials ? `(${detail.financials.year} 연간 · DART)` : ''}
                  </p>
                  {detail.financials ? (
                    <div className="grid grid-cols-2 gap-2">
                      <FinCell label="매출액" value={wonAbbr(detail.financials.revenue)} />
                      <FinCell label="영업이익" value={wonAbbr(detail.financials.op_profit)} />
                      <FinCell label="순이익" value={wonAbbr(detail.financials.net_income)} />
                      <FinCell label="ROE" value={detail.financials.roe != null ? `${detail.financials.roe}%` : '─'} />
                      <FinCell
                        label="매출성장(YoY)"
                        value={detail.financials.revenue_growth != null ? `${detail.financials.revenue_growth > 0 ? '+' : ''}${detail.financials.revenue_growth}%` : '─'}
                        good={(detail.financials.revenue_growth ?? 0) > 0}
                      />
                      <FinCell
                        label="순익성장(YoY)"
                        value={detail.financials.income_growth != null ? `${detail.financials.income_growth > 0 ? '+' : ''}${detail.financials.income_growth}%` : '─'}
                        good={(detail.financials.income_growth ?? 0) > 0}
                      />
                    </div>
                  ) : (
                    <p className="text-dc-ink3 text-[11.5px]">재무 데이터 없음 (해외/ETF 또는 미상장)</p>
                  )}
                </div>

                {/* D: 지분 이벤트 — 5%룰 대량보유·자사주 취득. 회사·큰손이 싸다고 판단한 재료(참고용). 있을 때만. */}
                {detail.ownership_events && detail.ownership_events.length > 0 && (
                  <div className={`${AMBERBOX} px-3.5 py-3`}>
                    <p className="text-dc-point text-[11.5px] mb-2 font-medium">🏷️ 지분 이벤트 <span className="text-dc-ink3 font-normal">— 5%룰·자사주(회사·큰손이 싸다고 본 신호)</span></p>
                    <ul className="space-y-1.5">
                      {detail.ownership_events.map((e, i) => (
                        <li key={i} className="flex items-start gap-2 text-[11.5px]">
                          <span className="font-mono text-dc-ink3 shrink-0">{e.date?.slice(4)}</span>
                          <span className={`shrink-0 ${MINICHIP} ${e.kind === '자사주' ? TONE.up : TONE.point}`}>{e.kind}</span>
                          {e.url ? (
                            <a href={e.url} target="_blank" rel="noreferrer" className="flex-1 leading-snug text-dc-ink2 hover:text-dc-point transition-colors">{e.label}</a>
                          ) : (
                            <span className="flex-1 leading-snug text-dc-ink2">{e.label}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* 공시 (DART) */}
                <div className={SECTION}>
                  <p className={`${KICKER} mb-3`}>최근 공시 (DART)</p>
                  {detail.disclosures && detail.disclosures.length > 0 ? (
                    <ul className="space-y-1.5">
                      {detail.disclosures.map((d, i) => (
                        <li key={i}>
                          <a
                            href={d.url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-start gap-2 text-[11.5px] text-dc-ink2 hover:text-dc-point transition-colors group"
                          >
                            <span className="font-mono text-dc-ink3 shrink-0">{d.date?.slice(4)}</span>
                            <span className="flex-1 leading-snug">{d.title}</span>
                            <ExternalLink size={11} className="shrink-0 mt-0.5 text-dc-ink3 group-hover:text-dc-point" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-dc-ink3 text-[11.5px]">최근 120일 공시 없음 (또는 해외/ETF)</p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
